import React, { Children } from "react";
import PropTypes from "prop-types";

/**
 * Variantes pré-definidas.
 * - Cada variante define bg e text (pode ajustar conforme seu design system).
 */
const VARIANTS = {
  blue:      { bg: "bg-primary hover:brightness-95",     text: "text-primary-foreground", ring: "focus-visible:ring-primary/40" },
  indigo:    { bg: "bg-primary hover:brightness-95",     text: "text-primary-foreground", ring: "focus-visible:ring-primary/40" },
  yellow:    { bg: "bg-amber-500 hover:bg-amber-600",    text: "text-white", ring: "focus-visible:ring-amber-400/40" },
  gray:      { bg: "bg-secondary hover:bg-muted",        text: "text-white", ring: "focus-visible:ring-muted-foreground/30" },
  grayLight: { bg: "bg-muted hover:bg-muted/70",         text: "text-muted-foreground", ring: "focus-visible:ring-muted-foreground/30" },
  white:     { bg: "bg-card hover:bg-muted border border-border", text: "text-card-foreground", ring: "focus-visible:ring-muted-foreground/30" },
  outline:   { bg: "bg-transparent hover:bg-muted border border-border", text: "text-foreground", ring: "focus-visible:ring-muted-foreground/30" },
  red:       { bg: "bg-destructive hover:brightness-95", text: "text-destructive-foreground", ring: "focus-visible:ring-destructive/30" },
  green:     { bg: "bg-success hover:brightness-95",     text: "text-success-foreground", ring: "focus-visible:ring-success/30" },
  orange:     { bg: "bg-orange-600 hover:bg-orange-700", text: "text-white", ring: "focus-visible:ring-orange-400/40" },
  blueLight:     { bg: "bg-accent hover:bg-accent/70",   text: "text-accent-foreground", ring: "focus-visible:ring-primary/30" },
};

/**
 * @typedef {"button"|"submit"|"reset"} ButtonType
 */

/**
 * @typedef {keyof typeof VARIANTS} ButtonVariant
 */

/**
 * @typedef ButtonProps
 * @property {ButtonType=} type - Tipo do botão.
 * @property {string=} title - Tooltip/title do botão.
 * @property {string=} className - Classes adicionais do botão.
 * @property {string=} classNameIcon - Classes adicionais do ícone.
 * @property {React.CSSProperties=} style - Estilo inline.
 * @property {boolean=} disabled - Desabilita o botão.
 * @property {boolean=} loading - Mostra estado de carregando e desabilita.
 * @property {string|React.ReactNode=} label - Texto (ou nó) do botão.
 * @property {string=} iconLeft - Classe do ícone esquerdo (ex.: "fas fa-plus").
 * @property {string=} iconRight - Classe do ícone direito.
 * @property {ButtonVariant=} variant - Variante de cor pré-definida (ex.: "indigo").
 * @property {string=} bgClass - Sobrescreve classe de background (ex.: "bg-indigo-600").
 * @property {string=} textClass - Sobrescreve classe de texto (ex.: "text-white").
 * @property {(e: React.MouseEvent<HTMLButtonElement>) => void=} onClick - Click handler.
 */

/**
 * Botão padrão do projeto.
 * @param {ButtonProps} props
 */
function Button(props) {
  const {
    type,
    style,
    title,
    className,
    disabled,
    loading,
    onClick,
    iconLeft,
    iconRight,
    classNameIcon,
    label,
    variant,
    bgClass,
    textClass,
    children,
    rounded
  } = props;

  // Pega a variante (fallback: blue)
  const v = VARIANTS[variant] || VARIANTS.blue;

  // Permite sobrescrever a cor via props (sem quebrar o default)
  const bg = bgClass || v.bg;
  const text = textClass || v.text;
  const ring = v.ring || "focus-visible:ring-primary/40";

  const isDisabled = Boolean(disabled || loading);

  const baseClasses = `
    inline-flex items-center justify-center
    h-9 px-3.5
    rounded-${rounded ? rounded : 'md'}
    text-sm font-medium
    shadow-xs
    transition-all duration-150
    active:translate-y-px
    disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0
    outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${ring}
  `;

  // Ícone: se loading, mostra spinner. Caso contrário, mostra a classe do ícone.
  const leftIconClass = loading
    ? `fas fa-spinner fa-spin ${text}`
    : `${iconLeft} ${classNameIcon || ""}`;

  const rightIconClass = loading
    ? `fas fa-spinner fa-spin ${text}`
    : `${iconRight} ${classNameIcon || ""}`;

  return (
    <button
      type={type}
      style={style}
      title={title}
      className={`${baseClasses} ${bg} ${text} ${className || ""}`}
      disabled={isDisabled}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 no-wrap">
        {iconLeft && (
          <i className={leftIconClass} aria-hidden="true" />
        )}

        {label || null}

        {iconRight && (
          <i className={rightIconClass} aria-hidden="true" />
        )}
      </div>
      {children}
    </button>
  );
}

Button.propTypes = {
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  title: PropTypes.string,
  className: PropTypes.string,
  classNameIcon: PropTypes.string,
  style: PropTypes.object,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconLeft: PropTypes.string,
  iconRight: PropTypes.string,
  rounded: PropTypes.string,
  variant: PropTypes.oneOf(Object.keys(VARIANTS)),
  bgClass: PropTypes.string,
  textClass: PropTypes.string,
  onClick: PropTypes.func,
};

Button.defaultProps = {
  type: "button",
  variant: "blue",
  loading: false,
  disabled: false,
};

export default Button;